// Lifecycle Section — joiners vs relieved snapshot.
//
// The backend doesn't currently expose a month-by-month time series, so
// rather than fake one, we surface the 30/90-day counts the summary
// endpoint already returns. We use `relieved_*` (Relieving_Date) rather
// than `resignations_*` (Resignation_date) because the source warehouse
// populates Relieving_Date for departures whereas Resignation_date is
// usually empty.

import React from 'react';
import { Box, Skeleton, Stack, Typography } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import LogoutIcon from '@mui/icons-material/Logout';

import type { HrStatsSummaryResponse } from '../../types/hrStats';
import { formatNumberFull } from '../../utils/FormatUtils';
import SectionCard from './primitives/SectionCard';
import { ACCENTS } from './primitives/peopleInsightsTokens';

export interface LifecycleSnapshotProps {
  summary?: HrStatsSummaryResponse;
}

const LifecycleSnapshot: React.FC<LifecycleSnapshotProps> = ({ summary }) => {
  if (!summary) {
    return (
      <SectionCard title="Lifecycle Snapshot">
        <Skeleton variant="rounded" height={120} />
      </SectionCard>
    );
  }

  const joinerNet = summary.joiners_30d - summary.relieved_30d;

  return (
    <SectionCard
      title="Joiners vs Relieved"
      subtitle="30-day and 90-day windows based on last working day (Relieving_Date). A month-by-month trend will be added once the data warehouse exposes it."
      icon={
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: 1.5,
            backgroundColor: ACCENTS.emerald.surface,
            color: ACCENTS.emerald.on,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <TrendingUpIcon fontSize="small" />
        </Box>
      }
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
          gap: 2,
        }}
      >
        <SnapshotCard
          accentSurface={ACCENTS.emerald.surface}
          accentMain={ACCENTS.emerald.main}
          accentText={ACCENTS.emerald.on}
          icon={<PersonAddAlt1Icon fontSize="small" />}
          title="Joiners"
          primary={formatNumberFull(summary.joiners_30d)}
          primaryLabel="last 30 days"
          secondary={formatNumberFull(summary.joiners_90d)}
          secondaryLabel="last 90 days"
        />
        <SnapshotCard
          accentSurface={ACCENTS.rose.surface}
          accentMain={ACCENTS.rose.main}
          accentText={ACCENTS.rose.on}
          icon={<LogoutIcon fontSize="small" />}
          title="Relieved"
          primary={formatNumberFull(summary.relieved_30d)}
          primaryLabel="last 30 days"
          secondary={formatNumberFull(summary.relieved_90d)}
          secondaryLabel="last 90 days"
        />
        <SnapshotCard
          accentSurface={joinerNet >= 0 ? ACCENTS.emerald.surface : ACCENTS.rose.surface}
          accentMain={joinerNet >= 0 ? ACCENTS.emerald.main : ACCENTS.rose.main}
          accentText={joinerNet >= 0 ? ACCENTS.emerald.on : ACCENTS.rose.on}
          icon={joinerNet >= 0 ? <TrendingUpIcon fontSize="small" /> : <TrendingDownIcon fontSize="small" />}
          title="Net Change (30d)"
          primary={`${joinerNet >= 0 ? '+' : ''}${formatNumberFull(joinerNet)}`}
          primaryLabel="joiners − relieved"
          secondary={`${formatNumberFull(summary.relieved_90d)}`}
          secondaryLabel="relieved last 90d"
        />
      </Box>
    </SectionCard>
  );
};

// ----------------------------------------------------------------------------

interface SnapshotCardProps {
  accentSurface: string;
  accentMain: string;
  accentText: string;
  icon: React.ReactNode;
  title: string;
  primary: string;
  primaryLabel: string;
  secondary: string;
  secondaryLabel: string;
}

const SnapshotCard: React.FC<SnapshotCardProps> = ({
  accentSurface,
  accentMain,
  accentText,
  icon,
  title,
  primary,
  primaryLabel,
  secondary,
  secondaryLabel,
}) => (
  <Box
    sx={{
      backgroundColor: accentSurface,
      borderRadius: 2,
      p: 2,
      borderLeft: `4px solid ${accentMain}`,
    }}
  >
    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: 1.25,
          backgroundColor: '#fff',
          color: accentText,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {icon}
      </Box>
      <Typography variant="caption" sx={{ fontWeight: 700, color: accentText, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        {title}
      </Typography>
    </Stack>
    <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: 'text.primary', lineHeight: 1.2 }}>
      {primary}
    </Typography>
    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
      {primaryLabel}
    </Typography>
    <Box sx={{ mt: 1.25, pt: 1, borderTop: '1px solid rgba(15, 23, 42, 0.05)' }}>
      <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>
        {secondary}
      </Typography>
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
        {secondaryLabel}
      </Typography>
    </Box>
  </Box>
);

export default LifecycleSnapshot;
